/**
 * Multi-Study Pre-Screener Component (Refactored)
 *
 * Simplified component that delegates all logic to useMultiStudyFlow hook.
 * Reduced from 290 lines to ~50 lines by separating concerns.
 *
 * Handles prescreening flow for multi-study landing pages:
 * 1. Ask common questions first (age, concurrent trial)
 * 2. Ask for zipcode to determine location eligibility
 * 3. Filter studies by location and priority
 * 4. Ask study-specific questions for the highest priority match
 * 5. Cascade to next study if disqualified (invisible to user)
 * 6. Store matched study for contact form submission
 */

import { PreScreeningQuestionnaire } from "../../../components/PreScreeningQuestionnaire";
import { useMultiStudyFlow } from "../hooks/useMultiStudyFlow";
import { Study } from "../types";
import { PreScreeningQuestion } from "../../../types/landingPage";

interface MultiStudyPreScreenerProps {
  landingPageId: string;
  commonQuestions: PreScreeningQuestion[];
  studies: Study[];
  onComplete: () => void;
}

export function MultiStudyPreScreener({
  landingPageId,
  commonQuestions,
  studies,
  onComplete,
}: MultiStudyPreScreenerProps) {
  const {
    flowState,
    currentQuestions,
    currentHandler,
    currentTitle,
    currentSubtitle,
    currentStudy,
    isLoading,
  } = useMultiStudyFlow({
    landingPageId,
    commonQuestions,
    studies,
    onComplete,
  });

  // Show loading state when fetching GraphQL data
  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-3">Finding available studies...</span>
      </div>
    );
  }

  // Don't render if flow is complete or no questions
  if (flowState.stage === "complete" || currentQuestions.length === 0) {
    return null;
  }

  // Determine if there are more studies to try (for seamless disqualification UX)
  const hasMoreStudiesToTry =
    flowState.stage === "study-specific" &&
    flowState.data.currentStudyIndex < flowState.data.eligibleStudies.length - 1;

  return (
    <PreScreeningQuestionnaire
      key={`${flowState.stage}-${
        flowState.stage === "study-specific" ? currentStudy?.id : ""
      }`}
      questions={currentQuestions}
      onNextScreen={currentHandler}
      landingPageId={landingPageId}
      title={currentTitle}
      subtitle={currentSubtitle}
      hasMoreStudiesToTry={hasMoreStudiesToTry}
      isMultiStudyFlow={true}
    />
  );
}
